import { BasicInfoSection } from '@/components/InvoiceReportDetailDrawer/BasicInfoSection';
import { SECTION_IDS } from '@/components/InvoiceReportDetailDrawer/constants';
import { usePaymentDetail } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import type { SectionCollapseStates } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionCollapseStates';
import { useSectionToggles } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionToggles';
import { TRANSACTION_MASTER_FIELD_NAMES } from '@/features/transaction_items/constants';
import { Loading } from '@moneyforward/ap-frontend-components';
import { GetInvoiceReportSummaryResponse } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import { ApplicationContent } from './ApplicationContent';
import { JournalInfo } from './JournalInfo';
import styles from './styles.module.scss';
import { TransactionsDetailSection } from './TransactionsDetail';

const cx = classnames.bind(styles);

type LeftContentProps = {
  data?: GetInvoiceReportSummaryResponse;
  isLoading: boolean;
  reportId: string;
  sectionStates?: SectionCollapseStates;
  onSectionToggle?: (sectionId: string, isOpen: boolean) => void;
};

const DEFAULT_EXCLUDE_COLUMNS = [TRANSACTION_MASTER_FIELD_NAMES.INVOICE_KIND];

export const LeftContent: FC<LeftContentProps> = memo(
  ({ data, isLoading, reportId, sectionStates, onSectionToggle }) => {
    const excludeColumns = DEFAULT_EXCLUDE_COLUMNS;
    const { isRightPaneCollapsed } = usePaymentDetail();

    const isSectionOpen = useCallback(
      (sectionId: string) => !sectionStates?.[sectionId],
      [sectionStates]
    );

    const sectionToggles = useSectionToggles(onSectionToggle);

    if (isLoading) {
      return (
        <div className={cx('loading-container')}>
          <Loading size='large' color='default' />
        </div>
      );
    }

    if (!data) return null;

    return (
      <div
        className={cx('left-content', { 'full-width': isRightPaneCollapsed })}
      >
        <BasicInfoSection
          data={data}
          initialOpen={isSectionOpen(SECTION_IDS.basicInfo)}
          onToggle={sectionToggles.basicInfo}
        />
        <ApplicationContent
          reportId={reportId}
          initialOpen={isSectionOpen(SECTION_IDS.applicationContent)}
          onToggle={sectionToggles.applicationContent}
        />
        <TransactionsDetailSection
          reportId={reportId}
          excludeColumns={excludeColumns}
          initialOpen={isSectionOpen(SECTION_IDS.transactionsDetail)}
          onToggle={sectionToggles.transactionsDetail}
        />
        <JournalInfo
          reportId={reportId}
          initialOpen={isSectionOpen(SECTION_IDS.journalInfo)}
          onToggle={sectionToggles.journalInfo}
        />
      </div>
    );
  }
);

LeftContent.displayName = 'LeftContent';
